/**
 * EmailLogPage — Küldési napló frontend UI
 * Story 3.2: Küldési napló frontend UI
 */

import type { EmailLogItem } from '@/api/email-templates';
import { listEmailLogs, resendEmail } from '@/api/email-templates';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useTranslation } from '@kgc/i18n';
import {
  Badge,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
} from '@kgc/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

const STATUS_BADGES: Record<
  string,
  { variant: 'default' | 'secondary' | 'destructive' | 'outline'; label: string }
> = {
  sent: { variant: 'default', label: '✅ Sent' },
  failed: { variant: 'destructive', label: '❌ Failed' },
  queued: { variant: 'outline', label: '⏳ Queued' },
  rejected: { variant: 'secondary', label: '🚫 Rejected' },
};

export function EmailLogPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [resendDialogId, setResendDialogId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['email-logs', statusFilter, page],
    queryFn: async () => {
      const res = await listEmailLogs({
        status: statusFilter || undefined,
        page,
        limit: 20,
      });
      return res.data;
    },
  });

  const resendMut = useMutation({
    mutationFn: async (logId: string) => {
      const res = await resendEmail(logId);
      return res.data;
    },
    onSuccess: () => {
      toast.success(t('email.resendSuccess'));
      setResendDialogId(null);
      void queryClient.invalidateQueries({ queryKey: ['email-logs'] });
    },
    onError: () => {
      toast.error(t('common.errorLabel'));
    },
  });

  const stats = data?.stats;
  const totalPages = data ? Math.ceil(data.total / data.limit) : 0;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">{t('email.emailLogs')}</h1>

      {/* Stats KPIs */}
      {stats && (
        <div className="grid grid-cols-4 gap-4 mb-6">
          {(
            [
              { key: 'sent', value: stats.sent, color: 'text-green-600 dark:text-green-400' },
              { key: 'failed', value: stats.failed, color: 'text-destructive' },
              { key: 'rejected', value: stats.rejected, color: 'text-muted-foreground' },
              { key: 'queued', value: stats.queued, color: 'text-blue-600 dark:text-blue-400' },
            ] as const
          ).map(s => (
            <Card key={s.key}>
              <CardContent className="py-4 text-center">
                <div className={`text-2xl font-bold ${s.color}`}>{s.value}</div>
                <div className="text-xs text-muted-foreground mt-1">{s.key}</div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-4 mb-4">
        <Select
          value={statusFilter}
          onValueChange={v => {
            setStatusFilter(v === 'all' ? '' : v);
            setPage(1);
          }}
        >
          <SelectTrigger className="w-48">
            <SelectValue placeholder={t('email.filterByStatus')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('email.filterByStatus')}</SelectItem>
            <SelectItem value="sent">✅ Sent</SelectItem>
            <SelectItem value="failed">❌ Failed</SelectItem>
            <SelectItem value="queued">⏳ Queued</SelectItem>
            <SelectItem value="rejected">🚫 Rejected</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-sm text-muted-foreground ml-auto">
          {data ? `${data.total} ${t('email.emailLogs')}` : ''}
        </span>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="text-left px-4 py-3 font-medium">{t('email.sentAt')}</th>
                <th className="text-left px-4 py-3 font-medium">{t('email.recipientEmail')}</th>
                <th className="text-left px-4 py-3 font-medium">{t('email.subject')}</th>
                <th className="text-left px-4 py-3 font-medium">{t('email.status')}</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={5} className="text-center py-8 text-muted-foreground">
                    {t('common.loading')}
                  </td>
                </tr>
              ) : !data?.items.length ? (
                <tr>
                  <td colSpan={5} className="text-center py-8 text-muted-foreground">
                    —
                  </td>
                </tr>
              ) : (
                data.items.map((log: EmailLogItem) => {
                  const badge = STATUS_BADGES[log.status] ?? STATUS_BADGES.queued!;
                  const isExpanded = expandedId === log.id;
                  return (
                    <tr key={log.id} className="border-b last:border-0 group">
                      <td className="px-4 py-3 text-muted-foreground">
                        {new Date(log.sentAt ?? log.createdAt).toLocaleString('hu-HU')}
                      </td>
                      <td className="px-4 py-3">{log.recipientEmail}</td>
                      <td className="px-4 py-3 truncate max-w-[300px]">{log.subject}</td>
                      <td className="px-4 py-3">
                        <Badge variant={badge.variant}>{badge.label}</Badge>
                      </td>
                      <td className="px-4 py-3 text-right space-x-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setExpandedId(isExpanded ? null : log.id)}
                        >
                          {isExpanded ? '▲' : '▼'}
                        </Button>
                        {log.status === 'failed' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setResendDialogId(log.id)}
                          >
                            {t('email.resend')}
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Expanded details (shown below table for simplicity) */}
      {expandedId &&
        data?.items.find(l => l.id === expandedId) &&
        (() => {
          const log = data.items.find(l => l.id === expandedId)!;
          return (
            <Card className="mt-2">
              <CardContent className="py-4 text-sm space-y-1">
                <div>
                  <span className="font-medium">Template:</span> {log.templateSlug}
                </div>
                <div>
                  <span className="font-medium">Job ID:</span> {log.jobId ?? '—'}
                </div>
                <div>
                  <span className="font-medium">Retry:</span> {log.retryCount}
                </div>
                {log.errorMessage && (
                  <div className="text-destructive">
                    <span className="font-medium">Error:</span> {log.errorMessage}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })()}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-4">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage(p => p - 1)}
          >
            ←
          </Button>
          <span className="text-sm text-muted-foreground">
            {page} / {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => setPage(p => p + 1)}
          >
            →
          </Button>
        </div>
      )}

      {/* Resend confirmation dialog */}
      <Dialog
        open={resendDialogId !== null}
        onOpenChange={open => {
          if (!open) setResendDialogId(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('email.resend')}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">{t('email.resendConfirm')}</p>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setResendDialogId(null)}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={() => resendDialogId && resendMut.mutate(resendDialogId)}
              disabled={resendMut.isPending}
            >
              {resendMut.isPending ? t('common.loading') : t('email.resend')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
